import moment from 'moment-timezone'
import {
  applyQilinXp,
  createQilinPet,
  qilinPetSummary,
  QILIN_PROGRESSION_CONFIG
} from './qilinProgression'

const PREVIEW_STORAGE_KEY = 'vr1game:qilin-preview-state-v2'
const CHECKIN_LADDER = [2, 2, 3, 3, 4, 4, 8]
const CHECKIN_MIN_DEPOSIT = 100
const PREVIEW_TODAY_DEPOSIT = 300
const PREVIEW_WEEK_DEPOSIT = 3000
const PREVIEW_WEEK_DEPOSIT_DAYS = 5
const PREVIEW_WEEK_CHECKINS_BEFORE_TODAY = 4

const MISSION_DEFINITIONS = Object.freeze([
  { id: 'm_checkin', scope: 'daily', coin: 1, xp: 5, target: 1 },
  { id: 'm_deposit300', scope: 'daily', coin: 3, xp: 15, target: 300 },
  { id: 'w_checkin5', scope: 'weekly', coin: 5, xp: 50, target: 5 },
  { id: 'w_depositdays5', scope: 'weekly', coin: 7, xp: 60, target: 5 },
  { id: 'w_deposit3000', scope: 'weekly', coin: 10, xp: 75, target: 3000 }
])

function todayMoment () {
  return moment().tz('Asia/Bangkok')
}

function clone (value) {
  return JSON.parse(JSON.stringify(value))
}

function previewError (code, message) {
  const error = new Error(message)
  error.code = `functions/${code}`
  return error
}

function defaultCheckinState (bkk) {
  const days = {}
  for (let offset = 4; offset >= 1; offset -= 1) {
    days[bkk.clone().subtract(offset, 'day').format('YYYY-MM-DD')] = true
  }
  return {
    streak: 4,
    bestStreak: 9,
    totalCheckins: 18,
    lastCheckinDate: bkk.clone().subtract(1, 'day').format('YYYY-MM-DD'),
    days
  }
}

function createPreviewState () {
  const bkk = todayMoment()
  return {
    version: 2,
    pet: createQilinPet(),
    checkin: defaultCheckinState(bkk),
    missionClaims: {}
  }
}

function loadPreviewState () {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PREVIEW_STORAGE_KEY) || 'null')
    if (!stored || stored.version !== 2) return createPreviewState()
    return {
      version: 2,
      pet: Object.assign(createQilinPet(), stored.pet || {}),
      checkin: Object.assign(defaultCheckinState(todayMoment()), stored.checkin || {}),
      missionClaims: Object.assign({}, stored.missionClaims || {})
    }
  } catch (error) {
    return createPreviewState()
  }
}

let previewState = loadPreviewState()

function persistPreviewState () {
  try {
    sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previewState))
  } catch (error) {
    // Preview still works until the page reloads when browser storage is unavailable.
  }
}

function petSummary () {
  return qilinPetSummary(previewState.pet, todayMoment().format('YYYY-MM-DD'))
}

function ladderReward (streak) {
  return CHECKIN_LADDER[(Math.max(1, streak) - 1) % CHECKIN_LADDER.length]
}

function currentCheckinStatus () {
  const bkk = todayMoment()
  const today = bkk.format('YYYY-MM-DD')
  const yesterday = bkk.clone().subtract(1, 'day').format('YYYY-MM-DD')
  const checkin = previewState.checkin
  const claimedToday = checkin.lastCheckinDate === today
  const currentStreak = checkin.lastCheckinDate === today || checkin.lastCheckinDate === yesterday
    ? Math.max(0, Number(checkin.streak) || 0)
    : 0
  const nextStreak = claimedToday
    ? currentStreak
    : (checkin.lastCheckinDate === yesterday ? currentStreak + 1 : 1)
  const monthPrefix = today.substring(0, 7)
  const monthDays = {}
  Object.keys(checkin.days || {}).forEach(date => {
    if (date.indexOf(monthPrefix) === 0) monthDays[date] = true
  })
  return {
    success: true,
    today,
    claimedToday,
    canClaim: !claimedToday && PREVIEW_TODAY_DEPOSIT >= CHECKIN_MIN_DEPOSIT,
    streak: currentStreak,
    bestStreak: Math.max(currentStreak, Number(checkin.bestStreak) || 0),
    totalCheckins: Math.max(0, Number(checkin.totalCheckins) || 0),
    lastCheckinDate: checkin.lastCheckinDate || '',
    monthDays,
    ladder: clone(CHECKIN_LADDER),
    todayDeposit: PREVIEW_TODAY_DEPOSIT,
    minDeposit: CHECKIN_MIN_DEPOSIT,
    nextStreak,
    nextReward: ladderReward(nextStreak),
    pet: petSummary()
  }
}

function missionPeriod (scope, bkk) {
  return scope === 'weekly' ? bkk.format('GGGG-[W]WW') : bkk.format('YYYY-MM-DD')
}

function missionClaimKey (mission, bkk) {
  return `${missionPeriod(mission.scope, bkk)}_${mission.id}`
}

function currentMissionStatus () {
  const bkk = todayMoment()
  const checkin = currentCheckinStatus()
  const checkedInToday = checkin.claimedToday
  const progressById = {
    m_checkin: checkedInToday ? 1 : 0,
    m_deposit300: Math.min(PREVIEW_TODAY_DEPOSIT, 300),
    w_checkin5: Math.min(PREVIEW_WEEK_CHECKINS_BEFORE_TODAY + (checkedInToday ? 1 : 0), 5),
    w_depositdays5: Math.min(PREVIEW_WEEK_DEPOSIT_DAYS, 5),
    w_deposit3000: Math.min(PREVIEW_WEEK_DEPOSIT, 3000)
  }
  const missions = MISSION_DEFINITIONS.map(definition => {
    const progress = Number(progressById[definition.id] || 0)
    return Object.assign({}, definition, {
      progress,
      done: progress >= definition.target,
      claimed: Boolean(previewState.missionClaims[missionClaimKey(definition, bkk)]),
      period: missionPeriod(definition.scope, bkk)
    })
  })
  return {
    success: true,
    pet: petSummary(),
    missions,
    daily: missions.filter(item => item.scope === 'daily'),
    weekly: missions.filter(item => item.scope === 'weekly'),
    claimableCount: missions.filter(item => item.done && !item.claimed).length,
    todayDeposit: PREVIEW_TODAY_DEPOSIT,
    weekDeposit: PREVIEW_WEEK_DEPOSIT
  }
}

function exposePreviewState () {
  if (typeof window === 'undefined') return
  window.render_game_to_text = () => JSON.stringify({
    mode: 'qilin-preview',
    pet: petSummary(),
    checkin: currentCheckinStatus(),
    missions: currentMissionStatus().missions
  })
  window.advanceTime = () => window.render_game_to_text()
}

export function resetQilinPreview () {
  previewState = createPreviewState()
  persistPreviewState()
  exposePreviewState()
  return petSummary()
}

exposePreviewState()

export default {
  getHomeStatus: async () => {
    const checkin = currentCheckinStatus()
    const missions = currentMissionStatus()
    return {
      success: true,
      enabled: true,
      mode: 'preview',
      pet: petSummary(),
      checkin: {
        claimedToday: checkin.claimedToday,
        canClaim: checkin.canClaim,
        todayDeposit: checkin.todayDeposit,
        minDeposit: checkin.minDeposit
      },
      missions: { claimableCount: missions.claimableCount }
    }
  },
  getPetStatus: async () => ({ success: true, pet: petSummary() }),
  play: async () => {
    const today = todayMoment().format('YYYY-MM-DD')
    if (previewState.pet.lastPlayedDate === today) {
      throw previewError('already-exists', 'Already played with the pet today')
    }
    const gain = applyQilinXp(previewState.pet, QILIN_PROGRESSION_CONFIG.xpPerPlay)
    previewState.pet = gain.next
    previewState.pet.lastPlayedDate = today
    persistPreviewState()
    return {
      success: true,
      xpGained: QILIN_PROGRESSION_CONFIG.xpPerPlay,
      bondXpGained: gain.bondXpGained,
      levelUps: gain.levelUps,
      coinBonus: gain.coinBonus,
      pet: petSummary()
    }
  },
  updateProfile: async payload => {
    const name = String(payload && payload.name ? payload.name : '').replace(/[<>]/g, '').trim().slice(0, 20)
    const gender = payload && payload.gender === 'male' ? 'male' : 'female'
    if (!name) throw previewError('invalid-argument', 'Invalid pet name')
    if (previewState.pet.gender && previewState.pet.gender !== gender) {
      throw previewError('failed-precondition', 'Pet gender cannot be changed')
    }
    const coinCharged = previewState.pet.name && previewState.pet.name !== name
      ? QILIN_PROGRESSION_CONFIG.renameCost
      : 0
    previewState.pet.name = name
    if (!previewState.pet.gender) previewState.pet.gender = gender
    persistPreviewState()
    return { success: true, pet: petSummary(), coinCharged }
  },
  getCheckinStatus: async () => currentCheckinStatus(),
  claimCheckin: async () => {
    const status = currentCheckinStatus()
    if (status.claimedToday) throw previewError('already-exists', 'Already checked in today')
    if (!status.canClaim) throw previewError('failed-precondition', 'Deposit insufficient')
    const gain = applyQilinXp(previewState.pet, QILIN_PROGRESSION_CONFIG.xpPerCheckin, {
      feed: true,
      today: status.today
    })
    previewState.pet = gain.next
    previewState.checkin.streak = status.nextStreak
    previewState.checkin.bestStreak = Math.max(status.nextStreak, Number(previewState.checkin.bestStreak) || 0)
    previewState.checkin.totalCheckins = Math.max(0, Number(previewState.checkin.totalCheckins) || 0) + 1
    previewState.checkin.lastCheckinDate = status.today
    previewState.checkin.days[status.today] = true
    persistPreviewState()
    return {
      success: true,
      reward: status.nextReward,
      streak: status.nextStreak,
      comebackBonus: 0,
      xpGained: QILIN_PROGRESSION_CONFIG.xpPerCheckin,
      bondXpGained: gain.bondXpGained,
      levelUps: gain.levelUps,
      levelCoinBonus: gain.coinBonus,
      totalCoin: status.nextReward + gain.coinBonus,
      pet: petSummary()
    }
  },
  getMissionsStatus: async () => currentMissionStatus(),
  claimMission: async missionId => {
    const bkk = todayMoment()
    const status = currentMissionStatus()
    const mission = status.missions.find(item => item.id === missionId)
    if (!mission) throw previewError('invalid-argument', 'Unknown mission')
    if (!mission.done) throw previewError('failed-precondition', 'Mission not completed')
    if (mission.claimed) throw previewError('already-exists', 'Mission reward already claimed')
    const gain = applyQilinXp(previewState.pet, mission.xp)
    previewState.pet = gain.next
    previewState.missionClaims[missionClaimKey(mission, bkk)] = true
    persistPreviewState()
    return {
      success: true,
      missionId: mission.id,
      coin: mission.coin,
      xpGained: mission.xp,
      bondXpGained: gain.bondXpGained,
      levelUps: gain.levelUps,
      levelCoinBonus: gain.coinBonus,
      totalCoin: mission.coin + gain.coinBonus,
      pet: petSummary()
    }
  }
}
